import { FormattedMessage, useIntl } from 'react-intl';
import { LOADING_TYPES, useCheckoutState } from '../../../utils/checkoutState';
import { config, getAssetUrl } from '../../../utils/config';
import { pushErrorTrackingEventFromErrorObject } from '../../../utils/tracking';
import InputWithIconAndButton from '../../atoms/InputWithIconAndButton';
import SubHeadline from '../../atoms/SubHeadline';
import { showDefaultError, showNotification } from '../Notifications';
import PointsRedemption from './PointsRedemption';
import ProgramActivation from './ProgramActivation';
import './style.scss';

const LoyaltyProgram = () => {
  const { formatMessage } = useIntl();
  const {
    checkoutState,
    isLoading,
    setLoading,
    registerForLoyaltyProgram,
    setLoyaltyProgram,
    removeLoyaltyProgram,
    redeemLoyaltyProgramPoints,
    unredeemLoyaltyProgramPoints
  } = useCheckoutState();

  const { type: membershipType, icon, redeemablePoints } = config.features.loyaltyProgram;
  const activeMembership = checkoutState.orderState.memberships?.activated?.[0];
  const suggestedMembership = checkoutState.orderState.memberships?.suggestions?.[0];

  const register = async () => {
    setLoading(LOADING_TYPES.loyaltyRegister);
    try {
      await registerForLoyaltyProgram();
    } catch (e) {
      console.error(e);
      showDefaultError();
      pushErrorTrackingEventFromErrorObject(e, 'register_for_loyalty_program');
    }
    setLoading(LOADING_TYPES.off);
  };

  const add = async (loyaltyProgramCode: string) => {
    setLoading(LOADING_TYPES.loyaltyAdd);
    try {
      await setLoyaltyProgram({
        membershipAccountNumber: loyaltyProgramCode,
        membershipTypeKey: suggestedMembership?.membershipTypeKey ?? membershipType
      });
    } catch (e: any) {
      if (e.statusCode === 400) {
        showNotification({
          level: 'error',
          text: formatMessage(
            { id: 'payment.loyaltyProgram.notifications.invalid' },
            { type: suggestedMembership?.membershipTypeKey ?? membershipType }
          ),
          autoClose: true
        });
      } else {
        showDefaultError();
      }
      console.error(e);
      pushErrorTrackingEventFromErrorObject(e, 'set_loyalty_program');
    }
    setLoading(LOADING_TYPES.off);
  };

  const remove = async () => {
    setLoading(LOADING_TYPES.loyaltyRemove);
    try {
      await removeLoyaltyProgram({
        membershipAccountNumber: activeMembership!.membershipAccountNumber,
        membershipTypeKey: activeMembership?.membershipTypeKey ?? membershipType
      });
    } catch (e) {
      console.error(e);
      showDefaultError();
      pushErrorTrackingEventFromErrorObject(e, 'remove_loyalty_program');
    }
    setLoading(LOADING_TYPES.off);
  };

  const redeem = async (redeemablePointBalance: number) => {
    setLoading(LOADING_TYPES.loyaltyRedeem);
    try {
      await redeemLoyaltyProgramPoints({ points: redeemablePointBalance });
    } catch (e) {
      console.error(e);
      showDefaultError();
      pushErrorTrackingEventFromErrorObject(e, 'redeem_loyalty_program');
    }
    setLoading(LOADING_TYPES.off);
  };

  const unredeem = async () => {
    setLoading(LOADING_TYPES.loyaltyUnredeem);
    try {
      await unredeemLoyaltyProgramPoints();
    } catch (e) {
      console.error(e);
      showDefaultError();
      pushErrorTrackingEventFromErrorObject(e, 'unredeem_loyalty_program');
    }
    setLoading(LOADING_TYPES.off);
  };

  if (!activeMembership) {
    return (
      <div className="loyalty-program">
        <ProgramActivation onSignIn={add} onRegister={register} />
      </div>
    );
  }

  return (
    <div className="loyalty-program">
      <SubHeadline>
        <FormattedMessage id="payment.loyaltyProgram.name" />
      </SubHeadline>
      <InputWithIconAndButton
        value={activeMembership.membershipAccountNumber}
        icon={icon.enabled ? { src: getAssetUrl('inputs/loyaltyProgram.svg') } : undefined}
        button={{
          text: 'generic.remove',
          dataTestId: 'payment-remove-payback',
          disabled: config.features.loyaltyProgram.disableRemoval || !!isLoading,
          onClick: remove
        }}
        placeholder={formatMessage({ id: 'payment.loyaltyProgram.label' })}
        isLoading={isLoading === LOADING_TYPES.loyaltyRemove}
        disabled
      />
      <p className="loyalty-program__earnings">
        <FormattedMessage id="payment.loyaltyProgram.legend" values={{ earnings: activeMembership?.earnings.points }} />
      </p>
      {redeemablePoints.enabled && (
        <PointsRedemption
          activeMembership={activeMembership}
          redeem={redeem}
          unredeem={unredeem}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};

export default LoyaltyProgram;
